import { useState, useCallback, useMemo, useRef } from 'react';
import { debounce } from 'lodash';

/**
 * Custom hook for searching within a single conversation's messages
 * Provides real-time search, highlighting, navigation, and filters
 */
export const useConversationSearch = (messages = [], currentUserId) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [isSearchActive, setIsSearchActive] = useState(false);
    const [currentMatch, setCurrentMatch] = useState(0);
    const [searchFilters, setSearchFilters] = useState({
        messageType: 'all', // 'all', 'text', 'media', 'audio', 'files'
        sender: 'all', // 'all', 'me', 'other'
        dateRange: 'all', // 'all', 'today', 'week', 'month'
        hasAttachments: 'all' // 'all', 'yes', 'no'
    });
    const [searchHistory, setSearchHistory] = useState([]);
    
    const searchInputRef = useRef(null);
    const lastSearchTime = useRef(Date.now());

    // Debounced search to avoid excessive filtering
    const debouncedSearch = useCallback(
        debounce((query) => {
            setSearchQuery(query);
            if (query.trim()) {
                setCurrentMatch(0);
                addToHistory(query);
            }
        }, 300),
        []
    );

    // Add search term to history
    const addToHistory = useCallback((term) => {
        if (term.trim() && !searchHistory.includes(term)) {
            setSearchHistory(prev => [term, ...prev.slice(0, 9)]); // Keep last 10 searches
        }
    }, [searchHistory]);

    // Filter messages based on search query and filters
    const filteredMessages = useMemo(() => {
        if (!searchQuery.trim() && Object.values(searchFilters).every(f => f === 'all')) {
            return messages;
        }

        return messages.filter(message => {
            // Text search
            const matchesText = !searchQuery.trim() || (
                message.content?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                message.sender_details?.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                message.sender_details?.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                message.sender_details?.username?.toLowerCase().includes(searchQuery.toLowerCase())
            );

            // Message type filter
            const matchesType = searchFilters.messageType === 'all' || (
                (searchFilters.messageType === 'text' && message.content && !message.audio && !message.attachments?.length) ||
                (searchFilters.messageType === 'audio' && message.audio) ||
                (searchFilters.messageType === 'media' && message.attachments?.some(att => 
                    att.type === 'image' || att.type === 'video'
                )) ||
                (searchFilters.messageType === 'files' && message.attachments?.some(att => 
                    att.type !== 'image' && att.type !== 'video'
                ))
            );

            // Sender filter
            const matchesSender = searchFilters.sender === 'all' || (
                (searchFilters.sender === 'me' && String(message.sender) === String(currentUserId)) ||
                (searchFilters.sender === 'other' && String(message.sender) !== String(currentUserId))
            );

            // Date range filter
            const matchesDate = searchFilters.dateRange === 'all' || (() => {
                const messageDate = new Date(message.timestamp);
                const now = new Date();
                const diffDays = Math.floor((now - messageDate) / (1000 * 60 * 60 * 24));

                switch (searchFilters.dateRange) {
                    case 'today':
                        return diffDays === 0;
                    case 'week':
                        return diffDays <= 7;
                    case 'month':
                        return diffDays <= 30;
                    default:
                        return true;
                }
            })();

            // Attachments filter
            const matchesAttachments = searchFilters.hasAttachments === 'all' || (
                (searchFilters.hasAttachments === 'yes' && (message.attachments?.length > 0 || message.audio)) ||
                (searchFilters.hasAttachments === 'no' && !message.attachments?.length && !message.audio)
            );

            return matchesText && matchesType && matchesSender && matchesDate && matchesAttachments;
        });
    }, [messages, searchQuery, searchFilters, currentUserId]);

    // Get search matches (messages that specifically match the text query)
    const searchMatches = useMemo(() => {
        if (!searchQuery.trim()) return [];
        
        return filteredMessages.filter(message => {
            return message.content?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                   message.sender_details?.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                   message.sender_details?.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                   message.sender_details?.username?.toLowerCase().includes(searchQuery.toLowerCase());
        });
    }, [filteredMessages, searchQuery]);

    // Navigation functions
    const navigateToNext = useCallback(() => {
        if (searchMatches.length === 0) return;
        setCurrentMatch(prev => (prev + 1) % searchMatches.length);
    }, [searchMatches.length]);

    const navigateToPrevious = useCallback(() => {
        if (searchMatches.length === 0) return;
        setCurrentMatch(prev => prev === 0 ? searchMatches.length - 1 : prev - 1);
    }, [searchMatches.length]);

    const navigateToMatch = useCallback((index) => {
        if (index >= 0 && index < searchMatches.length) {
            setCurrentMatch(index);
        }
    }, [searchMatches.length]);

    // Get current match message
    const currentMatchMessage = useMemo(() => {
        return searchMatches[currentMatch] || null;
    }, [searchMatches, currentMatch]);

    // Highlight function for message text
    const highlightSearchTerm = useCallback((text, isCurrentMatch = false) => {
        if (!searchQuery.trim() || !text) return text;
        
        const regex = new RegExp(`(${searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        return text.replace(regex, (match) => {
            const className = isCurrentMatch ? 'search-highlight-current' : 'search-highlight';
            return `<span class="${className}">${match}</span>`;
        });
    }, [searchQuery]);

    // Toggle search mode
    const toggleSearch = useCallback(() => {
        setIsSearchActive(prev => {
            const newActive = !prev;
            if (!newActive) {
                setSearchQuery('');
                setCurrentMatch(0);
            } else {
                // Focus search input when activating
                setTimeout(() => {
                    searchInputRef.current?.focus();
                }, 100);
            }
            return newActive;
        });
    }, []);

    // Clear search
    const clearSearch = useCallback(() => {
        setSearchQuery('');
        setCurrentMatch(0);
        setSearchFilters({
            messageType: 'all',
            sender: 'all',
            dateRange: 'all',
            hasAttachments: 'all'
        });
        debouncedSearch.cancel();
    }, [debouncedSearch]);

    // Update filter
    const updateFilter = useCallback((key, value) => {
        setSearchFilters(prev => ({
            ...prev,
            [key]: value
        }));
        setCurrentMatch(0);
    }, []);

    // Get search statistics
    const searchStats = useMemo(() => {
        return {
            totalResults: filteredMessages.length,
            searchMatches: searchMatches.length,
            currentMatch: searchMatches.length > 0 ? currentMatch + 1 : 0,
            isFiltered: Object.values(searchFilters).some(f => f !== 'all') || searchQuery.trim()
        };
    }, [filteredMessages.length, searchMatches.length, currentMatch, searchFilters, searchQuery]);

    // Performance metrics
    const performanceMetrics = useMemo(() => {
        const searchTime = Date.now() - lastSearchTime.current;
        return {
            searchTime,
            totalMessages: messages.length,
            filteredCount: filteredMessages.length,
            matchCount: searchMatches.length
        };
    }, [messages.length, filteredMessages.length, searchMatches.length]);

    return {
        // State
        searchQuery,
        isSearchActive,
        currentMatch,
        searchFilters,
        searchHistory,
        
        // Data
        filteredMessages,
        searchMatches,
        currentMatchMessage,
        searchStats,
        performanceMetrics,
        
        // Refs
        searchInputRef,
        
        // Actions
        setSearchQuery: debouncedSearch,
        setIsSearchActive,
        toggleSearch,
        clearSearch,
        updateFilter,
        navigateToNext,
        navigateToPrevious,
        navigateToMatch,
        highlightSearchTerm,
        addToHistory,
        
        // Utilities
        setSearchHistory
    };
};

export default useConversationSearch;
